<template>
  <base-todo-item
    class="TodoItem"
    :class="{
      'TodoItem': true,
      'TodoItem__task-selected': selected,
      'TodoItem--completed': item.status === 'COMPLETED',
      'TodoItem--completing': isCompleting,
      'TodoItem--newly-added': isNewlyAdded
    }"
    :item="item"
    @item-click="editItem"
  >
    <!-- Column 1: Completion Circle -->
    <template #checkbox>
      <div class="checkbox-wrapper" @mousedown.stop.prevent>
        <button
            class="completion-circle"
            :class="{'is-checked': item.status === 'COMPLETED' || isCompleting}"
            @click.stop="changeStatus"
            type="button"
            aria-label="Toggle task completion"
            v-b-tooltip.hover.left="item.status === 'COMPLETED' ? 'Mark as incomplete' : 'Mark as complete'"
        >
          <svg class="completion-circle__svg" viewBox="0 0 24 24" width="22" height="22">
            <circle class="completion-circle__ring" cx="12" cy="12" r="10" fill="none" stroke-width="2" />
            <path class="completion-circle__check" d="M7.5 12.5l3 3 6-6" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </template>

    <!-- Column 2: Task Content -->
    <template #content>
      <div
        class="todo-content"
        data-testid="contentEditTrigger"
      >
        <div :class="['todo-text', { 'todo-text--completed': isCompletedTask }]">
          {{ cleanContent(item.content).text }}
          <i class="fa fa-spinner fa-spin loading-spinner" v-if="isLoading"/>
          <!-- Animated "Just added" badge (takes precedence, shown for 5s after creation) -->
          <span v-if="isNewlyAdded" class="just-added-badge">
            <i class="fa fa-star"></i> Just added
          </span>
          <!-- Static "New today" badge (shown for tasks created today, but not if "Just added" is showing) -->
          <span v-else-if="isCreatedToday" class="new-today-badge">
            New today
          </span>
        </div>
      </div>
    </template>

    <!-- Column 3: Attachments -->
    <template #attachments>
      <div class="column-attachments">
        <div v-if="hasAttachments" class="attachment-icon-container">
          <i class="fa fa-paperclip indicator-icon" title="Has attachments"></i>
          <span class="count-badge attachment-count">{{ item.wikiPages.length }}</span>
        </div>
      </div>
    </template>

    <!-- Column 3.5: Subtasks -->
    <template #subtasks>
      <div class="column-subtasks">
        <div v-if="hasSubtasks" class="subtask-icon-container" :title="`${subtasksCount.completed}/${subtasksCount.total} subtasks completed`">
          <i class="fa fa-list-ul indicator-icon"></i>
          <span class="count-badge subtask-count" :class="{ 'all-complete': subtasksCount.completed === subtasksCount.total }">
            {{ subtasksCount.completed }}/{{ subtasksCount.total }}
          </span>
        </div>
      </div>
    </template>

    <!-- Column 4: Comments -->
    <template #comments>
      <div class="column-comments">
        <div v-if="(item.comments && item.comments.length > 0) || newComment"
             class="comment-icon-container"
             :class="{'has-unread-comments': newComment}">
          <i class="fa fa-comment indicator-icon" title="Comments"></i>
          <!-- Show count only if there are comments AND it's not unread (indicator takes precedence) -->
          <span v-if="!newComment && item.comments && item.comments.length > 0" class="count-badge comment-count">{{ item.comments.length }}</span>
          <!-- Show indicator only if unread -->
          <span v-if="newComment" class="unread-indicator" title="New comments">!</span>
        </div>
      </div>
    </template>

    <!-- Column 5: Patients -->
    <template #patients>
      <div class="column-patients">
        <div class="patient-container" v-if="cleanContent(item.content).patients && cleanContent(item.content).patients.length > 0">
          <div class="patient-tags">
            <span
              v-for="patient in cleanContent(item.content).patients.slice(0, 2)"
              :key="patient.id"
              class="patient-tag clickable"
              @click.stop="$emit('filter', 'patient', { id: patient.id, name: patient.name })"
            >
              <i class="fa fa-user-circle patient-icon"></i> {{ patient.name }}
            </span>
          </div>
          <span class="patient-count-badge" v-if="cleanContent(item.content).patients.length > 2">
            +{{ cleanContent(item.content).patients.length - 2 }}
          </span>
        </div>
      </div>
    </template>

    <!-- MOVED: Column 6: Status -->
    <template #status>
      <div class="column-status">
        <!-- Completed Status: Display badge and completer info vertically -->
        <div v-if="isCompletedTask" class="status-container completed-status-container" @click.stop="$emit('filter', 'status')">
          <span class="status-badge status-badge-completed">Completed</span>
          <div v-if="item.completedByEmpID" class="completer-info" :title="`Completed by ${getEmployeeName(item.completedByEmpID)}`">
            <div class="completer-avatar" :class="getAvatarClass(item.completedByEmpID)" :style="getPersonAvatar(item.completedByEmpID) ? `background-image: url(${getPersonAvatar(item.completedByEmpID)})` : ''">
              {{ !getPersonAvatar(item.completedByEmpID) ? getInitials(getEmployeeName(item.completedByEmpID)) : '' }}
            </div>
            <span class="completer-name">{{ getEmployeeName(item.completedByEmpID) }}</span>
          </div>
        </div>
        <!-- Overdue Status -->
        <div v-else-if="isOverdue" class="status-container" @click.stop="$emit('filter', 'status')">
          <span class="status-badge status-badge-overdue">Overdue</span>
        </div>
        <!-- Incomplete Status -->
        <div v-else class="status-container" @click.stop="$emit('filter', 'status')">
          <span class="status-badge status-badge-incomplete">Incomplete</span>
        </div>
      </div>
    </template>

    <!-- Column 7: Tags -->
    <template #tags>
      <div class="column-tags">
        <div class="tag-container">
          <span
            v-for="tag in item.tags"
            :key="tag"
            class="tag clickable"
            @click.stop="$emit('filter', 'tags', tag)"
          >
            {{ tag }}
          </span>
        </div>
      </div>
    </template>

    <!-- NEW: Column 7.5: Repeats -->
    <template #repeats>
      <div class="column-repeats">
        <span v-if="item.repeat && item.repeatFrequency"
              class="tag repeat-tag clickable"
              @click.stop="$emit('filter', 'repeat')">
          {{ formatRepeatFrequency(item.repeatFrequency) }}
        </span>
      </div>
    </template>

    <!-- Column 8: Due Date -->
    <template #due-date>
      <div class="column-dates">
        <div class="date-container" v-if="item.dueAt && allSettings">
          <i class="fa fa-calendar-alt"></i>
          <span v-if="isTaskDueToday(item) && !item.completedAt"
                class="badge badge-due-today clickable"
                @click.stop="$emit('filter', 'due', item.dueAtStr)">Due Today</span>
          <span v-else class="date-text clickable" @click.stop="$emit('filter', 'due', item.dueAtStr)">
            {{item.dueAtStr | dateformat(dateFormatStr)}}
          </span>
        </div>
      </div>
    </template>

    <!-- Column 9: Created By -->
    <template #creator>
      <div class="column-creator">
        <div class="creator-container" v-if="item.createdByEmpID">
          <div class="creator-avatar" :class="getAvatarClass(item.createdByEmpID)" :style="getPersonAvatar(item.createdByEmpID) ? `background-image: url(${getPersonAvatar(item.createdByEmpID)})` : ''" :title="getEmployeeName(item.createdByEmpID)">
            {{ !getPersonAvatar(item.createdByEmpID) ? getInitials(getEmployeeName(item.createdByEmpID)) : '' }}
          </div>
          <span class="creator-name person-name clickable" @click.stop="$emit('filter', 'creator', { id: item.createdByEmpID, name: getEmployeeName(item.createdByEmpID) })">
            {{ getEmployeeName(item.createdByEmpID) }}
          </span>
        </div>
      </div>
    </template>

    <!-- Column 10: Assignees -->
    <template #assignees>
      <div class="column-assignees">
        <!-- Check if assignees exist -->
        <div class="assignee-container" v-if="item.assignees && item.assignees.length > 0">
          <!-- Case 1: Exactly one assignee -->
          <div v-if="item.assignees.length === 1" class="creator-container">
            <div
              class="creator-avatar"
              :class="getAvatarClass(item.assignees[0])"
              :style="getPersonAvatar(item.assignees[0]) ? `background-image: url(${getPersonAvatar(item.assignees[0])})` : ''"
              :title="getEmployeeName(item.assignees[0])"
            >
              {{ !getPersonAvatar(item.assignees[0]) ? getInitials(getEmployeeName(item.assignees[0])) : '' }}
            </div>
            <span class="creator-name person-name clickable" @click.stop="$emit('filter', 'assignee', { id: item.assignees[0], name: getEmployeeName(item.assignees[0]) })">
              {{ getEmployeeName(item.assignees[0]) }}
            </span>
          </div>

          <!-- Case 2: Multiple assignees - FIXED VERSION -->
          <div v-else class="multiple-assignees">
            <!-- Show first assignee with same styling as single assignee -->
            <div class="creator-avatar" 
                 :class="getAvatarClass(item.assignees[0])" 
                 :style="getPersonAvatar(item.assignees[0]) ? `background-image: url(${getPersonAvatar(item.assignees[0])})` : ''" 
                 :title="getEmployeeName(item.assignees[0])">
              {{ !getPersonAvatar(item.assignees[0]) ? getInitials(getEmployeeName(item.assignees[0])) : '' }}
            </div>
            <span class="creator-name person-name clickable" @click.stop="$emit('filter', 'assignee', { id: item.assignees[0], name: getEmployeeName(item.assignees[0]) })">
              {{ getEmployeeName(item.assignees[0]) }}
            </span>
            <!-- Show count for additional assignees -->
            <span class="assignee-count" v-if="item.assignees.length > 1">
              +{{ item.assignees.length - 1 }}
            </span>
          </div>
        </div>
      </div>
    </template>

      <!-- Column 11: Actions -->
      <template #actions>
        <div class="column-actions">
          <button class="delete-button" @click.stop="showWhichDeleteModal" aria-label="Delete task">
            <i class="fa fa-trash"></i>
          </button>
        </div>

        <!-- Modals remain outside the slot structure -->
        <b-modal ref="delete" id="delete" title="Delete" body-class="bg-white">
          <p>Are you sure you want to delete this task?</p>
          <div slot="modal-footer" class="w-100">
            <b-button variant="danger" size="sm" class="float-right" @click="hideModal('delete')">No</b-button>
            <b-button variant="success" size="sm" class="float-right mr-3" @click="deleteItem">Yes</b-button>
          </div>
        </b-modal>

        <b-modal ref="delete-repeated" id="delete-repeated" title="Delete Repeated Task" body-class="bg-white">
          <p>This task is a repeated task. Are you sure you want to delete this task?</p>
          <div slot="modal-footer" class="w-100">
            <b-button variant="danger" size="sm" class="float-right" @click="hideWhichDelete">No</b-button>
            <b-button variant="success" size="sm" class="float-right mr-3" @click="deleteItem">Yes - All instances</b-button>
            <b-button variant="success" size="sm" class="float-right mr-3" @click="deleteThisInstanceOnly">Yes - Delete next instance only</b-button>
          </div>
        </b-modal>
      </template>
    <!-- The modals below are correctly placed as direct children -->
    <b-modal ref="delete" id="delete" title="Delete" body-class="bg-white">
        <p>Are you sure you want to delete this task?</p>
        <div slot="modal-footer" class="w-100">
          <b-button variant="danger" size="sm" class="float-right" @click="hideModal('delete')">No</b-button>
          <b-button variant="success" size="sm" class="float-right mr-3" @click="deleteItem">Yes</b-button>
        </div>
      </b-modal>

    <b-modal ref="delete-repeated" id="delete-repeated" title="Delete Repeated Task" body-class="bg-white">
      <p>This task is a repeated task. Are you sure you want to delete this task?</p>
      <div slot="modal-footer" class="w-100">
        <b-button variant="danger" size="sm" class="float-right" @click="hideWhichDelete">No</b-button>
        <b-button variant="success" size="sm" class="float-right mr-3" @click="deleteItem">Yes - All instances</b-button>
        <b-button variant="success" size="sm" class="float-right mr-3" @click="deleteThisInstanceOnly">Yes - Delete next instance only</b-button>
      </div>
    </b-modal>
  </base-todo-item>
</template>

<script>
/* eslint-disable */

import BaseTodoItem from './BaseTodoItem';
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';

/**
 * @module TodoItem
 */
export default {
  name: 'TodoItem',
  components: { BaseTodoItem },
  props: {
    /**
     *  @type TodoItem
     */
    item: {
      type: Object,
      required: true
    },
    moreThanOneItems: {
      type: Boolean,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    selected: {
      type: Boolean,
      required: false
    },
    isNewlyAdded: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  mixins: [mixin],

  created() {
    // console.log(`[TodoItem ${this.item.id}] Received item prop:`, this.item, `Has unreadCommentsList:`, this.item.unreadCommentsList);
  },

  data () {
    return {
      isCompleting: false,
      isEditing: false,
      contentLocal: '',
      selectedTaskID: null,
      personAvatars: {},
      avatarColors: [
        'bg-primary', 'bg-success', 'bg-info', 'bg-warning',
        'bg-danger', 'bg-gray', 'bg-gray-dark', 'bg-gray-light'
      ],
      fullMonths: [
          {month: 'January', val: 1}, {month: 'February', val: 2}, {month: 'March', val: 3},
          {month: 'April', val: 4}, {month: 'May', val: 5}, {month: 'June', val: 6},
          {month: 'July', val: 7}, {month: 'August', val: 8}, {month: 'September', val: 9},
          {month: 'October', val: 10}, {month: 'November', val: 11}, {month: 'December', val: 12},
      ]
    }
  },

  mounted() {
  },

  methods: {

    ...mapActions('tasks', ['addPersonalTask', 'updateTask', 'deleteTask', 'deleteThisInstanceOfRepeatedTask']),

    showModal(name) {
      this.$refs[name].show();
    },

    showWhichDeleteModal(event) {
      event.stopPropagation();
      let refName = this.item.repeat && this.item.status === 'INCOMPLETE' ? 'delete-repeated' : 'delete';
      this.showModal(refName);
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    hideWhichDelete() {
      let refName = this.item.repeat ? 'delete-repeated' : 'delete';
      this.hideModal(refName);
    },

    async changeStatus(event) {
      // Explicitly prevent event propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Prevent double-clicks during animation
      if (this.isCompleting) return;

      let status, completedAt, completedAtStr, completedByEmpID;

      let dueAt = moment(this.item.dueAt).toDate();
      let dueAtStr = moment(this.item.dueAt).format('MM/DD/YYYY');

      if(this.item.status === 'COMPLETED') {
        delete this.item.completedAt;
        delete this.item.completedAtStr;
        if(this.item.completedByEmpID)
          delete this.item.completedByEmpID;
        status = 'INCOMPLETE';
      } else if (this.item.status === 'INCOMPLETE') {
        completedAt = moment().toDate();
        completedAtStr = moment().format('MM/DD/YYYY');
        completedByEmpID = this.userID;
        status = 'COMPLETED';
        /*
          Now we want to check if it's a repeat task AND
          if the due date is either today or later BUT not before because if the due date was
          yesterday or before that, the nightly reset tasks would have already created a new task for it and so we don't
          need to do it again
        */
        let diff = moment(this.item.dueAtStr).diff(moment(), 'days');
        //console.log('diff', diff);

        if(this.item.repeat && diff >= 0) {
          // Calculate potential next due date
          const nextDueAtDate = this.getNextDueDate(this.item).toDate();
          const nextDueAtStr = moment(nextDueAtDate).format('MM/DD/YYYY');

          // console.log('Calculated next due date:', nextDueAtStr);
          // console.log('Current item details for check:', {
          //   createdBy: this.item.createdByEmpID,
          //   freq: this.item.repeatFrequency,
          //   on: this.item.repeatOn,
          //   day: this.item.repeatOnDay,
          //   month: this.item.repeatOnMonth,
          //   lastDay: this.item.repeatOnLastDayOfMonth
          // });

          // Check if a task for the next due date already exists
          const tasksToCheck = this.combinedTasksFromStore;
          const existingNextInstance = Array.isArray(tasksToCheck)
            ? tasksToCheck.find(existingTask => {
              // Basic checks first for efficiency
              if (existingTask.dueAtStr !== nextDueAtStr ||
                  existingTask.status !== 'INCOMPLETE' || // Must be an active, non-completed task
                  existingTask.createdByEmpID !== this.item.createdByEmpID ||
                  existingTask.repeatFrequency !== this.item.repeatFrequency) {
                return false;
              }

              // Frequency-specific checks to ensure it's the same sequence
              const freq = this.item.repeatFrequency;
              if (freq === 'Weekly' || freq === 'Bi-Weekly') {
                if (existingTask.repeatOn !== this.item.repeatOn) return false;
              } else if (freq === 'Monthly') {
                // --- REVISED Last Day / Specific Day Check ---
                const itemHasLastDay = this.item.repeatOnLastDayOfMonth === true;
                const existingHasLastDay = existingTask.repeatOnLastDayOfMonth === true;
                const itemHasDay = this.item.repeatOnDay != null && this.item.repeatOnDay !== '';
                const existingHasDay = existingTask.repeatOnDay != null && existingTask.repeatOnDay !== '';

                // If one uses last day and the other uses a specific day, they don't match
                if (itemHasLastDay !== existingHasLastDay) return false;
                // If both use specific day, the days must match
                if (itemHasDay && existingHasDay && String(existingTask.repeatOnDay) !== String(this.item.repeatOnDay)) return false;
                // If neither uses last day, and only one specifies a day, they don't match (covers edge cases)
                if (!itemHasLastDay && !existingHasLastDay && itemHasDay !== existingHasDay) return false;
                // --- END REVISED ---
              } else if (freq === 'Yearly') {
                if (String(existingTask.repeatOnMonth) !== String(this.item.repeatOnMonth)) return false; // Compare as strings
                // --- REVISED Last Day / Specific Day Check ---
                const itemHasLastDay = this.item.repeatOnLastDayOfMonth === true;
                const existingHasLastDay = existingTask.repeatOnLastDayOfMonth === true;
                const itemHasDay = this.item.repeatOnDay != null && this.item.repeatOnDay !== '';
                const existingHasDay = existingTask.repeatOnDay != null && existingTask.repeatOnDay !== '';

                if (itemHasLastDay !== existingHasLastDay) return false;
                if (itemHasDay && existingHasDay && String(existingTask.repeatOnDay) !== String(this.item.repeatOnDay)) return false;
                if (!itemHasLastDay && !existingHasLastDay && itemHasDay !== existingHasDay) return false;
                // --- END REVISED ---
              }

              // --- RE-INTRODUCED Content Comparison ---
              try {
                  const currentCleanText = this.cleanContent(this.item.content).text;
                  const existingCleanText = this.cleanContent(existingTask.content).text;
                  if (currentCleanText !== existingCleanText) {
                      // console.log(`Content mismatch: Current='${currentCleanText}', Existing='${existingCleanText}'`);
                      return false;
                  }
              } catch (e) {
                  console.error("Error comparing task content during duplicate check:", e);
                  return false; // Fail safely if cleanContent throws error
              }
              // --- END RE-INTRODUCED ---

              // console.log('Potential duplicate found:', existingTask.id, existingTask.content, existingTask.dueAtStr);
              return true; // If all checks pass, it's a match
            })
            : null; // If tasks array is not valid, assume no duplicate found

          if (!existingNextInstance) {
            // console.log('No existing task found for next due date. Creating new instance.');
            // Only create the new task if no duplicate was found
            const currentItemId = this.item.id; // Store current ID before deleting
            delete this.item.id; // Remove ID for new task creation

            let task = {
              ...this.item,
              createdAt: moment().toDate(),
              dueAt: nextDueAtDate, // Use calculated next due date
              dueAtStr: nextDueAtStr, // Use calculated next due date string
              // Ensure status is INCOMPLETE for the new task
              status: 'INCOMPLETE',
              // Remove completion details for the new task
              completedAt: null,
              completedAtStr: null,
              completedByEmpID: null
            }
            // SUBTASKS: Do NOT copy subtasks to the new repeating task instance
            // Each new instance should start fresh without subtasks
            if (task.subtasks) {
              delete task.subtasks;
            }
            // Restore original ID to the item being modified
            this.item.id = currentItemId;

            let notification = [];
            let payload = {
              task,
              ...(notification && { notification: notification }),
            }

            // console.log('new task payload', payload);
            await this.addPersonalTask(payload);
            // console.log('new task added');
          } else {
             // console.log('Existing task found for next due date', nextDueAtStr, '. Skipping creation of duplicate.');
          }
        }
      }

      let changeObj = {
        ...this.item,
        dueAt: dueAt,
        dueAtStr: dueAtStr,
        status: status,
        createdAt: moment(this.item.createdAt).toDate(),
        ...(completedAt && { completedAt: completedAt }),
        ...(completedAtStr && { completedAtStr: completedAtStr }),
        ...(completedByEmpID && { completedByEmpID: completedByEmpID }),
      }

      // When completing: show animation first, then emit after delay
      if (status === 'COMPLETED') {
        this.isCompleting = true;
        setTimeout(() => {
          this.isCompleting = false;
          this.$emit('change', changeObj);
        }, 1200);
      } else {
        // Uncompleting: emit immediately
        this.$emit('change', changeObj);
      }
    },

    getEmployeeName(employeeID) {
      if(this.employees) {
        let users = this.employees.filter(e => e.EmployeeID === employeeID);
        if(users.length > 0)
          return users[0].Name;
      } else
        return 'N/A';
    },

    getEmployeePic(employeeID) {
      if(this.employees) {
        let users = this.employees.filter(e => e.EmployeeID === employeeID);
        if(users.length > 0)
          return users[0].PictureUrl;
      }
    },

    getInitials(name) {
      if (!name) return '';

      const parts = name.split(' ');
      if (parts.length === 1) return parts[0].charAt(0);

      return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    },

    /**
     * Deletes a todo item
     */
    deleteItem () {
      /*
      this.emitChange({
        deletedAt: Date.now()
      })*/
      this.$emit('delete', {
        ...this.item,
        deletedAt: Date.now(),
        // Add permanentDelete flag when deleting repeated tasks
        permanentDelete: this.item.repeat && this.item.status === 'INCOMPLETE'
      });
      //this.hideModal('delete');
      this.hideWhichDelete();
    },

    deleteThisInstanceOnly() {
      this.$emit('deleteThisInstanceOnly', {
        ...this.item,
        deletedAt: Date.now()
      });
      this.hideWhichDelete();
    },

    editItem() {
      // Emit only the 'edit' event with the full item object
      this.$emit('edit', this.item);
    },

    /**
     * Emits the changes to the TODO item up to the parent.
     * @param {Object} updatedItem - the todo item
     */
    emitChange (updatedItem) {
      this.$emit('change', {
        ...this.item,
        ...updatedItem
      })
    },
    /**
     * Starts the editing of a todo item.
     * Focuses the input item immediately
     * @return {Promise<void>}
     */
    async enableEdit () {
      this.contentLocal = this.item.content
      this.isEditing = true
      await this.$nextTick()
      this.$refs.editInput.focus()
    },
    /**
     * Saves the editing changes.
     * If no content is provided, just closes
     */
    saveEdit () {
      // console.log(`save edit called`);
      if (this.contentLocal) {
        this.emitChange({
          content: this.contentLocal
        })
      }
      this.stopEdit()
    },
    /**
     * Stops the editing, without saving anything
     */
    stopEdit () {
      this.isEditing = false
      this.contentLocal = ''
    },

    repeatDescription(task) {
            let desc = `This task repeats ${task.repeatFrequency}`;

            if(task.repeatOn && task.repeatFrequency === 'Weekly' && task.repeatFrequency === 'Bi-Weekly') {
                desc = desc + ` on ${task.repeatOn}`;
            }

            if(task.repeatOnDay && task.repeatFrequency !== 'Daily' && task.repeatFrequency !== 'Weekly' && task.repeatFrequency !== 'Bi-Weekly') {
                desc = desc + ` on ${task.repeatOnDay}`;
                switch(task.repeatOnDay) {
                    case 1 : desc = `${desc}st`; break;
                    case 2 : desc = `${desc}nd`; break;
                    case 3 : desc = `${desc}rd`; break;
                    default: desc = `${desc}th`; break;
                }
            }

            if(task.repeatOnMonth) {
                desc = desc + ` of ${moment().month(task.repeatOnMonth).format("MMM")}`;
            }

            return desc;
        },

      getPersonAvatar(person) {
        if (this.employees) {
          const employee = this.employees.find(e => e.EmployeeID === person);
          // Check for valid PictureUrl - must exist, not be "null" string, and not be empty
          if (employee && employee.PictureUrl && 
              employee.PictureUrl !== 'null' && 
              employee.PictureUrl !== 'undefined' &&
              employee.PictureUrl.trim() !== '') {
            return employee.PictureUrl;
          }
        }
        return null;
      },

      getAvatarClass(person) {
        if (this.getPersonAvatar(person)) {
          return '';
        }

        if (!person) return 'bg-gray-200 text-gray-700';

        let hash = 0;
        for (let i = 0; i < person.length; i++) {
          hash = person.charCodeAt(i) + ((hash << 5) - hash);
        }

        const colorIndex = Math.abs(hash) % this.avatarColors.length;
        return this.avatarColors[colorIndex];
      },

      cleanContent(content) {
        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;

        // Initialize result object
        const result = {
          text: '',
          employees: [],
          patients: [],
          tags: []
        };

        // Function to process text nodes
        const processNode = (node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent.trim();
          }

          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if this is a badge
            if (node.classList.contains('badge')) {
              if (node.hasAttribute('data-teamio-employeeid')) {
                const employeeId = node.getAttribute('data-teamio-employeeid');
                const employeeName = node.textContent.trim();
                result.employees.push({ id: employeeId, name: employeeName });
                return ''; // Don't include employee name in text
              }
              if (node.hasAttribute('data-teamio-patientid')) {
                const patientId = node.getAttribute('data-teamio-patientid');
                const patientName = node.textContent.trim();
                result.patients.push({ id: patientId, name: patientName });
                return ''; // Don't include patient name in text
              }
              // If it's a badge but not an employee or patient, it's a tag
              const tagText = node.textContent.trim();
              if (tagText) {
                result.tags.push(tagText);
              }
              return ''; // Don't include tag text in main content
            }

            // Process child nodes
            return Array.from(node.childNodes)
              .map(processNode)
              .filter(text => text)
              .join(' ');
          }

          return '';
        };

        // Process all nodes and get text content
        result.text = processNode(tempDiv);

        // Clean up extra whitespace in text
        result.text = result.text.replace(/\s+/g, ' ').trim();

        return result;
      },

    /**
     * Format the repeat frequency for display
     */
    formatRepeatFrequency(frequency) {
      if (!frequency) return '';
      if (!this.item) return `Repeats ${frequency.toLowerCase()}`; // Use item prop here

      const dayNamesFull = { Sun: 'Sunday', Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday' };

      switch(frequency) {
          case 'Daily': {
              let dailyText = 'Repeats daily';
              const excludedDays = [];
              if (this.item.excludeSaturday) excludedDays.push('Saturday');
              if (this.item.excludeSunday) excludedDays.push('Sunday');
              if (excludedDays.length > 0) {
                  dailyText += ` (excluding ${excludedDays.join(' and ')})`;
              }
              return dailyText;
          }
          case 'Weekly':
              return this.item.repeatOn && dayNamesFull[this.item.repeatOn] ? `Repeats weekly on ${dayNamesFull[this.item.repeatOn]}` : 'Repeats weekly';
          case 'Bi-Weekly':
              return this.item.repeatOn && dayNamesFull[this.item.repeatOn] ? `Repeats bi-weekly on ${dayNamesFull[this.item.repeatOn]}` : 'Repeats bi-weekly';
          case 'Monthly':
              if (this.item.repeatOnLastDayOfMonth === 'yes') {
                  return 'Repeats last day of every month';
              }
              return this.item.repeatOnDay ? `Repeats ${this.getOrdinal(this.item.repeatOnDay)} of every month` : 'Repeats monthly';
          case 'Yearly':
              if (this.item.repeatOnDay && this.item.repeatOnMonth) {
                  const monthName = this.fullMonths.find(m => m.val === parseInt(this.item.repeatOnMonth))?.month || '';
                  if (this.item.repeatOnLastDayOfMonth === 'yes') {
                       return `Repeats last day of ${monthName} every year`;
                  }
                  return `Repeats every ${monthName} ${this.getOrdinal(this.item.repeatOnDay)}`;
              }
              return 'Repeats yearly';
          default:
              return `Repeats ${frequency.toLowerCase()}`; // Fallback
      }
    },

    // *** NEW: Ordinal Number Helper ***
    getOrdinal(n) {
        if (n == null) return '';
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    // *** END NEW ***
  },

  computed: {
    ...mapState({
        userID: state => state.customer.userID,
        employees: state => state.customer.employees
    }),

    ...mapGetters({
        'isTaskDueToday' : 'tasks/isTaskDueToday',
        'getNextDueDate': 'tasks/getNextDueDate',
        'myCreatedTasksFromStore': 'tasks/myCreatedTasks',
        'assignedToMeTasksFromStore': 'tasks/assignedToMeTasks',
        'myEmployeesTasksFromStore': 'tasks/myEmployeesTasks'
    }),

    combinedTasksFromStore() {
      // Concatenate the three arrays, ensuring they are arrays first
      const created = Array.isArray(this.myCreatedTasksFromStore) ? this.myCreatedTasksFromStore : [];
      const assigned = Array.isArray(this.assignedToMeTasksFromStore) ? this.assignedToMeTasksFromStore : [];
      const employees = Array.isArray(this.myEmployeesTasksFromStore) ? this.myEmployeesTasksFromStore : [];
      // Use a Set to efficiently remove duplicates based on task ID
      const allTasksMap = new Map();
      [...created, ...assigned, ...employees].forEach(task => {
        if (task && task.id) { // Ensure task and task.id exist
          allTasksMap.set(task.id, task);
        }
      });
      return Array.from(allTasksMap.values());
    },

    hasAttachments() {
      return this.item && this.item.wikiPages && Array.isArray(this.item.wikiPages) && this.item.wikiPages.length > 0;
    },

    /**
     * Check if task was created today (for "New today" badge)
     */
    isCreatedToday() {
      if (!this.item || !this.item.createdAt) return false;

      // Handle Firestore Timestamp or JS Date
      let createdDate;
      if (this.item.createdAt.toDate) {
        // Firestore Timestamp
        createdDate = this.item.createdAt.toDate();
      } else if (this.item.createdAt instanceof Date) {
        createdDate = this.item.createdAt;
      } else {
        // Try parsing as string/number
        createdDate = new Date(this.item.createdAt);
      }

      if (isNaN(createdDate.getTime())) return false;

      // Compare with today's date (ignoring time)
      const today = new Date();
      return (
        createdDate.getFullYear() === today.getFullYear() &&
        createdDate.getMonth() === today.getMonth() &&
        createdDate.getDate() === today.getDate()
      );
    },
    
    hasSubtasks() {
      return this.item && this.item.subtasks && Array.isArray(this.item.subtasks) && this.item.subtasks.length > 0;
    },
    
    subtasksCount() {
      if (!this.hasSubtasks) return { total: 0, completed: 0 };
      const total = this.item.subtasks.length;
      const completed = this.item.subtasks.filter(st => st.status === 'COMPLETED').length;
      return { total, completed };
    },

    canDeleteTask() {
        return this.allSettings && this.allSettings.TASKS_WHO_CAN_DELETE && this.allSettings.TASKS_WHO_CAN_DELETE.PropertyValue.toUpperCase() === 'ANYONE' ? true : this.item.createdByEmpID === this.userID;
    },

    pastDue() {
      return moment(this.item.dueAt).isBefore(moment(), 'day');
    },

    newComment() {
      const list = this.item.unreadCommentsList;
      const user = this.userID;
      const result = list && Array.isArray(list) && list.indexOf(user) > -1;
      // console.log(`[TodoItem ${this.item.id}] newComment check: UserID=${user}, List=`, list, `Is Array=${Array.isArray(list)}, Result=${result}`);
      return result;
    },

    isCompletedTask() {
      return this.item.status === 'COMPLETED';
    },

    isOverdue() {
      // Only overdue if not completed and due date is in the past
      return !this.isCompletedTask && moment(this.item.dueAt).isBefore(moment(), 'day');
    }
  }
}
</script>

<style lang='scss' scoped>
.TodoItem {
  cursor: pointer;
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  display: grid;
  /* RESPONSIVE: Reduced fixed widths to match ToDo.vue header */
  grid-template-columns:
    36px             /* 1: Checkbox */
    minmax(0, 1fr)   /* 2: Content - flexible */
    40px             /* 3: Attachments */
    40px             /* 3.5: Subtasks */
    40px             /* 4: Comments */
    140px            /* 5: Patients */
    85px             /* 6: Status */
    100px            /* 7: Tags */
    110px            /* 8: Repeats */
    85px             /* 9: Due Date */
    95px             /* 10: Creator */
    115px            /* 11: Assignees - increased from 95px to fit +N badge */
    36px;            /* 12: Actions */
  column-gap: 6px; /* Reduced gap to match header */

  align-items: center;
  opacity: 1;
  background-color: white;
  position: relative;
  z-index: 1;

  &:hover {
    background-color: #f9fafb;

    .delete-button {
      opacity: 0.9;
    }
  }

  &__task-selected {
    background-color: #e6f7f2; /* Light green background */
    border: 1px solid #a3e6d1; /* Slightly darker green border */
    border-left: 4px solid #3abf94; /* Accent border */
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease-in-out;
  }

  &__task-selected:hover {
    background-color: #d1f0e8; /* Slightly darker on hover */
  }

  /* Style for completed tasks */
  &--completed {
    opacity: 0.75;
    background-color: #f9fafb;

    .todo-text {
      text-decoration: line-through;
      color: #94a3b8;
    }

    .date-text, .tag, .indicator-icon, .badge, .avatar {
      opacity: 0.7;
    }

    .column-status .badge-success {
      opacity: 1;
    }
  }
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 10;
  width: 100%;
  height: 100%;
}

/* Completion circle - styled to look like a task completion indicator, not a radio button */
.completion-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  position: relative;
  z-index: 10;

  .completion-circle__svg {
    transition: transform 0.15s ease;
  }

  .completion-circle__ring {
    stroke: #cbd5e1;
    transition: stroke 0.2s ease, fill 0.2s ease;
    fill: transparent;
  }

  .completion-circle__check {
    stroke: transparent;
    transition: stroke 0.2s ease;
  }

  &:hover .completion-circle__ring {
    stroke: #3abf94;
  }

  &:hover .completion-circle__check {
    stroke: #d1fae5;
  }

  &.is-checked .completion-circle__ring {
    stroke: #3abf94;
    fill: #3abf94;
  }

  &.is-checked .completion-circle__check {
    stroke: white;
  }

  &:hover .completion-circle__svg {
    transform: scale(1.1);
  }
}

/* Completing animation - task is being marked complete */
.TodoItem--completing {
  .todo-text {
    text-decoration: line-through;
    color: #94a3b8;
    transition: all 0.3s ease;
  }

  .date-text, .tag, .indicator-icon, .badge, .avatar {
    opacity: 0.5;
    transition: opacity 0.3s ease;
  }
}

/* Slide-out animation after completion delay */
.TodoItem--completing {
  animation: taskSlideOut 0.4s ease-in 0.8s forwards;
}

@keyframes taskSlideOut {
  0% {
    opacity: 1;
    max-height: 60px;
    transform: translateX(0);
  }
  50% {
    opacity: 0;
    transform: translateX(20px);
  }
  100% {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    margin-top: 0;
    margin-bottom: 0;
    overflow: hidden;
    transform: translateX(20px);
  }
}

.loading-spinner {
  margin-left: 0.5rem;
  color: #3abf94;
  font-size: 1rem;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  vertical-align: middle;
}

@keyframes pulse {
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
}

.todo-content {
  min-width: 0;
  overflow: hidden;
  padding: 0.125rem 0;
  position: relative;
  transition: transform 0.15s ease;

  &:hover .todo-text {
    color: #1e293b;
  }
}

.todo-text {
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 450;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  letter-spacing: 0.01em;

  // Apply strikethrough ONLY to the todo text when completed
  &--completed {
    text-decoration: line-through;
    color: #94a3b8;
  }
}

/* Column styling */
.column-tags,
.column-status,
.column-dates,
.column-assignees,
.column-actions,
.column-repeats {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  text-align: center;
}

/* Icon columns with count badges need overflow visible */
.column-attachments,
.column-comments {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  text-align: center;
  overflow: visible;
}

.column-tags {
  max-width: 100px;
  justify-content: flex-start;
  overflow: hidden;
}

.column-patients {
  display: flex;
  align-items: center;
  padding-right: 4px;
  min-width: 100px;
}

.patient-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
  position: relative;
}

.patient-tags {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
  max-width: 130px;
}

.patient-tag {
  background-color: #f1f5f9;
  color: #334155;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 80px;
  max-width: 120px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 20px;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  letter-spacing: 0.01em;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.patient-count-badge {
  background-color: #f1f5f9;
  color: #334155;
  font-weight: 600;
  font-size: 0.75rem;
  border-radius: 12px;
  padding: 0.1rem 0.4rem;
  position: absolute;
  right: 0;
  top: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.patient-simple-count {
  display: none;
}

.patient-icon {
  font-size: 0.75rem;
  color: #64748b;
  opacity: 0.9;
}

.column-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 100px;
}

.tag-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
}

.tag {
  background-color: #dbeafe; /* Match repeat-tag background */
  color: #1d4ed8; /* Match repeat-tag color */
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08); /* Match patient tag shadow */
}

.indicator-icon {
  font-size: 0.95rem;
  color: #94a3b8;
  transition: color 0.15s ease;

  &:hover {
    color: #64748b;
  }
}

.date-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;

  i {
    color: #64748b;
    font-size: 0.875rem;
    margin-right: 0.125rem;
  }

  .badge-due-today {
    background-color: #fb923c;
    background-image: linear-gradient(45deg, #fb923c, #f97316);
    font-weight: 600;
    letter-spacing: 0.02em;
    padding: 0.15rem 0.625rem;
    border-radius: 4px;
    text-shadow: 0 1px 1px rgba(0,0,0,0.1);
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
}

.date-text {
  color: #334155;
  font-size: 0.875rem;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  font-weight: 500;
  letter-spacing: 0.01em;
  background-color: #f1f5f9;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
}

.badge {
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 500;
  color: white;
  white-space: nowrap;
  letter-spacing: 0.01em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;

  &-danger {
    background-color: #ef4444;
    background-image: linear-gradient(135deg, #ef4444, #dc2626);
  }

  &-success {
    background-color: #10b981;
    background-image: linear-gradient(135deg, #10b981, #059669);
  }

  &-due-today {
    background-color: #f97316;
    background-image: linear-gradient(135deg, #fb923c, #f97316);
    font-weight: 600;
  }
}

.assignee-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  overflow: visible;
  width: 100%;
  min-width: 0;
}

/* Removed old complex multiple assignees CSS - now using simplified approach */

.assignee-count {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
  background-color: #f1f5f9;
  padding: 0.1rem 0.4rem;
  border-radius: 12px;
  margin-left: 0.25rem;
  flex-shrink: 0; /* Prevent badge from shrinking */
  display: inline-block; /* Ensure it displays inline */
  white-space: nowrap; /* Prevent wrapping */
  position: relative; /* Ensure it's positioned correctly */
  z-index: 10; /* Ensure it appears above other elements */
  min-width: fit-content; /* Ensure it takes up necessary space */
}

.column-actions {
  position: relative;
  width: 40px;
  justify-self: end;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  z-index: 5; /* Ensure it's above the item */
}

.delete-button {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  opacity: 0.6;
  transition: all 0.2s ease;
  margin-right: 2px;

  &:hover {
    background-color: #fee2e2;
    color: #ef4444;
    opacity: 1;
  }

  i {
    font-size: 1.125rem;
  }
}

.empty-status {
  min-height: 1.5rem; /* Maintain consistent height */
}

/* ============================================ */
/* Media Queries - Synced with ToDo.vue breakpoints */
/* Exclusive ranges — no overlapping queries */
/* ============================================ */

@media (max-width: 1400px) {
  .TodoItem {
    grid-template-columns:
      36px minmax(160px, 1fr) 36px 36px 36px 120px 80px 90px 100px 80px 90px 110px 36px;
    column-gap: 4px;
  }
}

@media (max-width: 1200px) {
  .TodoItem {
    grid-template-columns:
      36px minmax(150px, 1fr) 36px 36px 110px 75px 85px 95px 75px 85px 105px 36px;
    column-gap: 4px;
  }
  .column-comments { display: none !important; }
}

/* ============================================ */
/* SHARED CARD STYLES (<= 1024px) */
/* Premium card design for tablet + phone */
/* ============================================ */
@media (max-width: 1024px) {
  .TodoItem {
    min-width: 0;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.15s ease, border-color 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
  }

  /* Task title — bold, wrapping, clear hierarchy */
  .todo-text {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
    word-wrap: break-word;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.45;
    color: #1e293b;
  }

  .todo-content {
    overflow: visible;
  }

  /* OVERDUE — impossible to miss */
  .status-badge {
    font-size: 0.6875rem;
    padding: 3px 10px;
    border-radius: 20px;
    white-space: nowrap;
    font-weight: 600;
    letter-spacing: 0.02em;
  }
  .status-badge-overdue {
    background-color: #dc2626;
    color: #ffffff;
    font-weight: 700;
    box-shadow: 0 1px 4px rgba(220, 38, 38, 0.25);
  }
  .status-badge-completed {
    background-color: #dcfce7;
    color: #166534;
  }
  .status-badge-incomplete {
    background-color: #f1f5f9;
    color: #64748b;
  }
  .column-status { overflow: visible; }

  /* Patient — distinct styling */
  .patient-tag {
    font-size: 0.7rem;
    color: #0369a1;
    font-weight: 500;
    padding: 1px 6px;
    background: #f0f9ff;
    border-radius: 4px;
    border: 1px solid #bae6fd;
  }

  /* Date */
  .date-text, .badge-due-today {
    font-size: 0.7rem;
    font-weight: 500;
    color: #475569;
  }
  .badge-due-today {
    color: #dc2626;
    font-weight: 600;
  }

  /* Tags — colored pills */
  .column-tags .tag {
    font-size: 0.625rem;
    padding: 2px 7px;
    border-radius: 10px;
    font-weight: 500;
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  }

  /* Repeats — blue pill */
  .column-repeats .repeat-tag {
    font-size: 0.625rem;
    padding: 2px 7px;
    border-radius: 10px;
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
  }

  /* Creator/Assignee avatars */
  .creator-avatar {
    width: 22px;
    height: 22px;
    min-width: 22px;
    font-size: 0.55rem;
    border: 1.5px solid white;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  }
  .person-name {
    font-size: 0.7rem;
    color: #475569;
    font-weight: 500;
  }
  .assignee-count {
    font-size: 0.6rem;
    padding: 1px 5px;
    background: #e2e8f0;
    color: #334155;
    border-radius: 10px;
    font-weight: 600;
  }

  /* Icons */
  .indicator-icon {
    font-size: 0.75rem;
    color: #94a3b8;
  }
  .count-badge {
    font-size: 0.6rem;
    font-weight: 600;
  }

  /* Delete button — subtle until hover */
  .delete-button {
    font-size: 0.75rem;
    opacity: 0.3;
    color: #94a3b8;
    transition: opacity 0.15s, color 0.15s;

    &:hover {
      opacity: 1;
      color: #ef4444;
    }
  }
}

/* ============================================ */
/* TABLET CARD (641px - 1024px) */
/* ============================================ */
@media (min-width: 641px) and (max-width: 1024px) {
  .TodoItem {
    grid-template-columns: 28px minmax(0, 1fr) minmax(0, 1fr) 24px;
    grid-template-rows: repeat(6, auto);
    row-gap: 4px;
    column-gap: 8px;
    padding: 12px 10px;
    margin-bottom: 6px;
    max-width: 100%;
    box-sizing: border-box;
  }

  .todo-text {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
    word-wrap: break-word;
  }

  .todo-content {
    overflow: visible;
  }
}

/* ============================================ */
/* PHONE CARD (<= 640px) */
/* ============================================ */
@media (max-width: 640px) {
  .TodoItem {
    grid-template-columns: 24px minmax(0, 1fr) minmax(0, 1fr) 24px;
    grid-template-rows: repeat(6, auto);
    row-gap: 3px;
    column-gap: 6px;
    padding: 10px 8px;
    margin-bottom: 8px;
    max-width: 100%;
    box-sizing: border-box;
  }

  .todo-text {
    font-size: 0.8125rem;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
    word-wrap: break-word;
  }

  .todo-content {
    overflow: visible;
  }
}

/* Small phone (<= 420px) */
@media (max-width: 420px) {
  .TodoItem {
    padding: 10px 8px;
    margin-bottom: 6px;
    column-gap: 3px;
  }
  .todo-text {
    font-size: 0.8rem;
  }
  .status-badge {
    font-size: 0.625rem;
    padding: 2px 7px;
  }
  .creator-avatar {
    width: 18px;
    height: 18px;
    min-width: 18px;
  }
}

.debug-info {
  font-size: 0.6rem;
  color: #ff0000;
  position: absolute;
  top: -10px;
  background: #ffff00;
  padding: 1px;
  z-index: 999;
}

.repeat-tag {
  background-color: #dbeafe; /* Restore original blue background */
  color: #1d4ed8; /* Restore original blue text color */
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  font-weight: 500;
  font-size: 0.75rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.mr-1 {
  margin-right: 0.25rem;
}

.status-container {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
  cursor: pointer;

  &:hover .status-badge {
    opacity: 0.8;
  }
}

.column-creator {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  text-align: left;
  padding-left: 0.15rem;
  width: 100%;
}

.creator-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
}

.creator-avatar {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.6rem;
  color: white;
  background-color: #3b82f6;
  border: 1px solid white;
  background-size: cover;
  background-position: center;
}

/* Shared style for creator/assignee names */
.person-name {
  font-size: 0.75rem;
  color: #475569;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
}

.creator-name {
  flex: 1;
  min-width: 0;
}

.column-assignees {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  text-align: left;
  padding-left: 0;
  overflow: visible;
  width: 100%;
}

.attachment-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0.25rem;
  position: relative;
  overflow: visible;
}

.column-subtasks {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  text-align: center;
  overflow: visible;
}

.subtask-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0.25rem;
  position: relative;
  overflow: visible;
}

.subtask-icon-container .indicator-icon {
  color: #3abf94;
}

.subtask-count {
  background-color: #d1fae5;
  color: #065f46;
}

.subtask-count.all-complete {
  background-color: #10b981;
  color: #ffffff;
}

.count-badge {
  background-color: #f1f5f9;
  color: #475569;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  margin-left: 0.25rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.comment-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0.25rem;
  position: relative; /* Needed for absolute positioning of indicator */
  overflow: visible;
}

/* Style for the unread indicator */
.unread-indicator {
  position: absolute;
  top: 0;
  right: -2px;
  background-color: #ef4444; /* Red background */
  color: white;
  font-size: 0.6rem;
  font-weight: bold;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1; /* Adjust line height if needed */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Optional: Style the main icon differently when unread */
.comment-icon-container.has-unread-comments .indicator-icon {
  color: #3b82f6; /* Blue color for unread */
  font-weight: bold; /* Make icon bolder */
}

/* Status Badge Styling */
.status-badge {
  padding: 0.15rem 0.5rem;
  border-radius: 1rem; /* Pill shape */
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  display: inline-block;
  line-height: 1.2;
  white-space: nowrap;
}

.status-badge-completed {
  background-color: #dcfce7; /* Light green */
  color: #166534; /* Dark green */
}

.status-badge-overdue {
  background-color: #fef2f2; /* Light red */
  color: #dc2626; /* Dark red */
}

.status-badge-incomplete {
  background-color: #f1f5f9; /* Light grey */
  color: #64748b; /* Dark grey */
}

/* NEW: Styles for completed status container */
.completed-status-container {
  display: flex; /* Use flexbox */
  flex-direction: column; /* Stack items vertically */
  align-items: center; /* Center items horizontally */
  gap: 0.15rem; /* Small gap between badge and info */
}

/* NEW: Styles for completer info */
.completer-info {
  display: flex;
  align-items: center;
  gap: 0.25rem; /* Small gap between avatar and name */
  font-size: 0.75rem; /* Smaller font size */
  color: #64748b; /* Muted text color */
  margin-top: 0.1rem; /* Add small top margin for spacing below badge */
  cursor: default; /* Default cursor for tooltip */
}

.completer-avatar {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  font-size: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
  background-color: #cbd5e1; /* Default background */
}

.completer-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px; /* Limit name width */
}

/* Container for multiple assignees */
.multiple-assignees {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
  overflow: visible;
}

/* END NEW */

/* Removed conflicting size rule - avatars use their specific class sizes */

/* Adjustments for avatar classes based on completer-avatar size */
.completer-avatar.avatar-text {
  /* Styles for text avatar if needed */
}

/* END NEW */

/* NEW: Styling for Repeats column */
.column-repeats {
  justify-content: flex-start; /* Align content to start */
  overflow: hidden; /* Prevent overflow */
  min-width: 100px; /* Give it some minimum space */
}

.clickable {
  cursor: pointer;
}

.clickable:hover {
  text-decoration: underline;
  opacity: 0.8;
}

.tag {
  color: #666;
}

.tag:hover {
  color: #333;
}

.patient-name, .creator-name, .assignee-name {
  color: #2c3e50;
}

.patient-name:hover, .creator-name:hover, .assignee-name:hover {
  color: #1a2634;
}

/* Newly added task highlight animation */
.TodoItem--newly-added {
  animation: highlightPulse 5s ease-out;
}

@keyframes highlightPulse {
  0% {
    background-color: rgba(72, 187, 120, 0.35); /* Green highlight */
    box-shadow: 0 0 0 3px rgba(72, 187, 120, 0.4);
  }
  50% {
    background-color: rgba(72, 187, 120, 0.2);
    box-shadow: 0 0 0 2px rgba(72, 187, 120, 0.25);
  }
  100% {
    background-color: transparent;
    box-shadow: none;
  }
}

/* "Just Added" inline badge */
.just-added-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #047857; /* Emerald 700 */
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); /* Emerald gradient */
  border-radius: 12px;
  white-space: nowrap;
  animation: badgeFadeOut 5s ease-out forwards;
  box-shadow: 0 1px 2px rgba(4, 120, 87, 0.15);
}

.just-added-badge i {
  font-size: 10px;
}

@keyframes badgeFadeOut {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  70% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.95);
  }
}

/* "New today" static badge - subtler, persistent until midnight */
.new-today-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 500;
  color: #6b7280; /* Gray 500 */
  background-color: #f3f4f6; /* Gray 100 */
  border: 1px solid #e5e7eb; /* Gray 200 */
  border-radius: 10px;
  white-space: nowrap;
}
</style>
